import {
  clearStoredLogoutRoute,
  getStoredLogoutRoute,
  storeLogoutRoute,
  type LogoutRoute,
} from './logoutRoute';

export interface AuthRole {
  id: number;
  name: string;
}

export interface AuthUser {
  id: number;
  username: string;
  name: string;
  nexusCaregiverId?: string | null;
  nexusAccountId?: string | null;
  roles: AuthRole[];
  nexusCaregiver?: {
    nexusId: string;
    firstName: string;
    lastName: string;
    mobilePhone: string;
    email: string | null;
    status: string;
    portalStatus: string;
    primaryLanguage: string | null;
  } | null;
  nexusUser?: {
    nexusId: string;
    name?: string;
    firstName?: string;
    lastName?: string;
    mobilePhone?: string;
    portalRoles?: string[] | string;
  } | null;
}

const ACCESS_TOKEN_KEY = 'linkPortalAccessToken';
const REFRESH_TOKEN_KEY = 'linkPortalRefreshToken';
const REFRESH_TOKEN_VERSION_KEY = 'linkPortalRefreshTokenVersion';
const USER_KEY = 'linkPortalUser';
const AUTH_BROADCAST_CHANNEL = 'linkPortalAuth';
const AUTH_BROADCAST_STORAGE_KEY = 'linkPortalAuthBroadcast';
const AUTH_SESSION_EPOCH_KEY = 'linkPortalAuthSessionEpoch';
export const NURSE_ADMIN_SESSION_KEY = 'linkPortalNurseAdminSession';
export const NURSING_SELECTED_PATIENT_KEY = 'linkPortalNursingSelectedPatientId';
export const REFERRAL_SESSION_KEY = 'linkPortalReferralSession';
export const AUTH_CHANGE_EVENT = 'linkPortalAuthChange';

let refreshInFlight: Promise<string | null> | null = null;
let authListenersInitialized = false;
let authChannel: BroadcastChannel | null = null;

export class AuthExpiredError extends Error {
  constructor(message = 'Your session expired. Please sign in again to continue.') {
    super(message);
    this.name = 'AuthExpiredError';
  }
}

function notifyAuthChange(): void {
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT));
}

function decodeJwtPayload(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(normalizedPayload)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function refreshTokenVersion(refreshToken: string | null, fallback?: number): number {
  const payload = decodeJwtPayload(refreshToken);
  const version = Number(payload?.version ?? fallback ?? 0);
  return Number.isFinite(version) ? version : 0;
}

function refreshTokenSessionId(refreshToken: string | null): string {
  const payload = decodeJwtPayload(refreshToken);
  return typeof payload?.sessionId === 'string' ? payload.sessionId : '';
}

function storedAuthSessionEpoch(): number {
  const stored = Number(localStorage.getItem(AUTH_SESSION_EPOCH_KEY) || '0');
  return Number.isFinite(stored) ? stored : 0;
}

function nextAuthSessionEpoch(): number {
  return Math.max(Date.now(), storedAuthSessionEpoch() + 1);
}

function storedRefreshTokenVersion(): number {
  const stored = Number(localStorage.getItem(REFRESH_TOKEN_VERSION_KEY) || '0');
  return Number.isFinite(stored) ? stored : refreshTokenVersion(getRefreshToken());
}

function storeAuthTokens(
  accessToken: string,
  refreshToken: string,
  refreshTokenVersionValue?: number,
  broadcast = true,
  authSessionEpoch = storedAuthSessionEpoch(),
  user?: AuthUser
): void {
  const version = refreshTokenVersion(refreshToken, refreshTokenVersionValue);
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  localStorage.setItem(REFRESH_TOKEN_VERSION_KEY, String(version));
  if (authSessionEpoch) localStorage.setItem(AUTH_SESSION_EPOCH_KEY, String(authSessionEpoch));
  if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  notifyAuthChange();
  if (broadcast) broadcastAuthTokens(accessToken, refreshToken, version, authSessionEpoch, user);
}

function acceptAuthTokens(
  accessToken: string,
  refreshToken: string,
  refreshTokenVersionValue?: number,
  authSessionEpoch = 0,
  user?: AuthUser
): boolean {
  const incomingVersion = refreshTokenVersion(refreshToken, refreshTokenVersionValue);
  const incomingSessionId = refreshTokenSessionId(refreshToken);
  const currentSessionId = refreshTokenSessionId(getRefreshToken());
  const currentVersion = storedRefreshTokenVersion();
  const currentEpoch = storedAuthSessionEpoch();
  if (authSessionEpoch && authSessionEpoch < currentEpoch) return false;
  if (incomingSessionId === currentSessionId && incomingVersion < currentVersion) return false;
  storeAuthTokens(accessToken, refreshToken, incomingVersion, false, authSessionEpoch || currentEpoch, user);
  return true;
}

function broadcastAuthTokens(
  accessToken: string,
  refreshToken: string,
  refreshTokenVersionValue: number,
  authSessionEpoch: number,
  user?: AuthUser
): void {
  ensureAuthListeners();
  const message = {
    type: 'tokens',
    accessToken,
    refreshToken,
    refreshTokenVersion: refreshTokenVersionValue,
    authSessionEpoch,
    user,
  };
  authChannel?.postMessage(message);
  localStorage.setItem(AUTH_BROADCAST_STORAGE_KEY, JSON.stringify({ ...message, sentAt: Date.now() }));
}

function handleAuthBroadcast(data: unknown): void {
  const message = data as {
    type?: string;
    accessToken?: string;
    refreshToken?: string;
    refreshTokenVersion?: number;
    authSessionEpoch?: number;
    user?: AuthUser;
  };
  if (message?.type !== 'tokens' || !message.accessToken || !message.refreshToken) return;
  acceptAuthTokens(
    message.accessToken,
    message.refreshToken,
    message.refreshTokenVersion,
    message.authSessionEpoch,
    message.user
  );
}

function ensureAuthListeners(): void {
  if (authListenersInitialized || typeof window === 'undefined') return;
  authListenersInitialized = true;
  if ('BroadcastChannel' in window) {
    authChannel = new BroadcastChannel(AUTH_BROADCAST_CHANNEL);
    authChannel.onmessage = (event) => handleAuthBroadcast(event.data);
  }
  window.addEventListener('storage', (event) => {
    if (event.key !== AUTH_BROADCAST_STORAGE_KEY || !event.newValue) return;
    try {
      handleAuthBroadcast(JSON.parse(event.newValue));
    } catch {
      // Ignore malformed storage events from older tabs or manual edits.
    }
  });
}

export function getStoredUser(): AuthUser | null {
  try {
    const value = localStorage.getItem(USER_KEY);
    return value ? (JSON.parse(value) as AuthUser) : null;
  } catch {
    return null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

type StoreAuthSessionOptions = {
  preserveNursingSelection?: boolean;
  refreshTokenVersion?: number;
  logoutRoute?: LogoutRoute;
};

export function storeAuthSession(
  accessToken: string,
  refreshToken: string,
  user: AuthUser,
  options: StoreAuthSessionOptions = {}
): void {
  ensureAuthListeners();
  storeAuthTokens(accessToken, refreshToken, options.refreshTokenVersion, true, nextAuthSessionEpoch(), user);
  if (!options.preserveNursingSelection) {
    localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
  }
  if (options.logoutRoute) {
    storeLogoutRoute(options.logoutRoute);
  }
  notifyAuthChange();
}

export function storeAuthUser(user: AuthUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  notifyAuthChange();
}

export function hasRole(user: AuthUser | null, roleName: string): boolean {
  return user?.roles.some((role) => role.name === roleName) ?? false;
}

export type CaregiverFormRoute = '/application' | '/onboarding' | '/nursing-assessment';

export function getCaregiverFormRoute(user: AuthUser | null): CaregiverFormRoute | null {
  if (hasRole(user, 'app')) return '/application';
  if (hasRole(user, 'hha')) return '/onboarding';
  if (hasRole(user, 'nurse') || hasRole(user, 'nurse-admin')) return '/nursing-assessment';
  return null;
}

export function canAccessCaregiverFormRoute(user: AuthUser | null, route: CaregiverFormRoute): boolean {
  if (!user) return false;
  if (hasRole(user, 'admin')) return true;
  if (route === '/nursing-assessment') return hasRole(user, 'nurse') || hasRole(user, 'nurse-admin');
  if (route === '/application') return hasRole(user, 'app');
  if (route === '/onboarding') return hasRole(user, 'hha');
  return false;
}

export function getDefaultAuthenticatedRoute(user: AuthUser | null): string {
  if (hasRole(user, 'referral') && !getCaregiverFormRoute(user)) return '/referral';
  if (hasRole(user, 'quick-entry') && !getCaregiverFormRoute(user)) return '/qe-landing';
  return getCaregiverFormRoute(user) ?? '/start';
}

export function clearAuthStorage(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_VERSION_KEY);
  localStorage.removeItem(AUTH_SESSION_EPOCH_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(NURSE_ADMIN_SESSION_KEY);
  localStorage.removeItem(NURSING_SELECTED_PATIENT_KEY);
  localStorage.removeItem(REFERRAL_SESSION_KEY);
  clearStoredLogoutRoute();
  notifyAuthChange();
}

export async function logoutUser(): Promise<LogoutRoute> {
  const logoutRoute = getStoredLogoutRoute();
  const sessionEpoch = storedAuthSessionEpoch();
  try {
    await authFetch('/api/auth/logout', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: getRefreshToken() }),
    });
  } catch (error) {
    console.error('[Auth] Error logging out:', error);
  } finally {
    if (storedAuthSessionEpoch() === sessionEpoch) {
      clearAuthStorage();
    }
  }
  return logoutRoute;
}

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = refreshAccessTokenOnce().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

async function refreshAccessTokenOnce(): Promise<string | null> {
  ensureAuthListeners();
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) return null;
  const accessTokenBeforeRefresh = localStorage.getItem(ACCESS_TOKEN_KEY);
  const authSessionEpochBeforeRefresh = storedAuthSessionEpoch();

  const response = await fetch('/api/auth/refresh', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });

  if (!response.ok) {
    const latestAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const latestRefreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (latestRefreshToken && latestRefreshToken !== refreshToken && latestAccessToken && !accessTokenExpiresSoon(latestAccessToken)) {
      return latestAccessToken;
    }
    if (latestAccessToken && latestAccessToken !== accessTokenBeforeRefresh && !accessTokenExpiresSoon(latestAccessToken)) {
      return latestAccessToken;
    }
    clearAuthStorage();
    return null;
  }

  const data = await response.json();
  if (storedAuthSessionEpoch() > authSessionEpochBeforeRefresh) {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }
  storeAuthTokens(data.accessToken, data.refreshToken, data.refreshTokenVersion);
  return data.accessToken;
}

export async function refreshStoredUser(): Promise<AuthUser | null> {
  if (!getAccessToken()) {
    const refreshedToken = await refreshAccessToken();
    if (!refreshedToken) return null;
  }

  const response = await authFetch('/api/auth/me');
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.message || 'Failed to refresh user');
  }

  const user = data.user as AuthUser;
  storeAuthUser(user);
  return user;
}

function accessTokenExpiresSoon(token: string | null): boolean {
  if (!token) return false;

  try {
    const decoded = decodeJwtPayload(token) as { exp?: number } | null;
    if (!decoded?.exp) return false;

    const oneMinuteFromNow = Math.floor(Date.now() / 1000) + 60;
    return decoded.exp <= oneMinuteFromNow;
  } catch {
    return false;
  }
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}): Promise<Response> {
  const withAuth = (token: string | null): RequestInit => {
    const headers = new Headers(init.headers);
    if (token) headers.set('Authorization', `Bearer ${token}`);
    return { ...init, headers };
  };

  let token = getAccessToken();
  if (!token || accessTokenExpiresSoon(token)) {
    token = await refreshAccessToken() ?? token;
  }

  let response = await fetch(input, withAuth(token));
  if (response.status === 401 || (response.status === 403 && !token)) {
    const refreshedToken = await refreshAccessToken();
    if (refreshedToken) {
      response = await fetch(input, withAuth(refreshedToken));
      if (response.status === 401) {
        throw new AuthExpiredError();
      }
    } else {
      throw new AuthExpiredError();
    }
  }
  return response;
}

ensureAuthListeners();
